import { useState } from "react";

import { GridFeatureMode, GridValidRowModel } from "@mui/x-data-grid";
import { DataGridPro, GridColDef, GridPaginationModel, GridSortModel } from "@mui/x-data-grid-pro";
import { GridInitialStatePro } from "@mui/x-data-grid-pro/models/gridStatePro";

import { UserAnalyticsService } from "@analytics/index";

import { getRowClassName, tableStyles } from "@components/Table/tableStyles";

import { CompanyType } from "@interfaces/company.type";

import TableOverlay from "@pages/Companies/components/TableOverlay";

interface IDirectoryCompanyTableProperties<T extends GridValidRowModel> {
  columns: GridColDef<T>[];
  handlePaginationModelChange: (paginationModel: GridPaginationModel) => void;
  handleRowFocus: (rowSelection: CompanyType, field: string) => void;
  handleSortModelChange: (sortModel: GridSortModel) => void;
  initialState: GridInitialStatePro;
  isCompanySearching: boolean;
  noRowsLabel?: string;
  pagination: GridPaginationModel;
  paginationMode: GridFeatureMode;
  rowCount?: number;
  rows: T[];
  selection: string[];
  setSelection: (selection: string[]) => void;
  sortingMode: GridFeatureMode;
  sortModel: GridSortModel;
}

export default function CompaniesTable<T extends GridValidRowModel>({
  columns,
  handlePaginationModelChange,
  handleRowFocus,
  handleSortModelChange,
  isCompanySearching,
  noRowsLabel,
  pagination,
  paginationMode,
  rowCount,
  rows,
  selection,
  setSelection,
  sortingMode,
  sortModel,
}: IDirectoryCompanyTableProperties<T>) {
  const [sortCount, setSortCount] = useState(0);

  return (
    <DataGridPro
      autosizeOptions={{
        columns: ["sector"],
        expand: true,
        includeHeaders: true,
        includeOutliers: true,
        outliersFactor: 1.5,
      }}
      checkboxSelection
      checkboxSelectionVisibleOnly
      columns={columns}
      disableColumnMenu={true}
      disableRowSelectionOnClick
      getRowClassName={getRowClassName}
      indeterminateCheckboxAction="select"
      loading={isCompanySearching}
      localeText={{
        noRowsLabel: noRowsLabel ?? "No Results Found. Try updating your search term or filters.",
      }}
      onCellClick={(cellParameters) => {
        // Prevents checkbox and menu rows from triggering focus
        handleRowFocus(cellParameters.row, cellParameters.field);
      }}
      onPaginationModelChange={(model) => {
        handlePaginationModelChange(model);
      }}
      onRowSelectionModelChange={(newSelection) => {
        setSelection(newSelection.map((newSelection) => newSelection.toString()));
      }}
      onSortModelChange={(newSortModel) => {
        const newSortCount = sortCount + 1;
        if (newSortModel[0] && newSortModel[0].field && newSortModel[0].sort) {
          handleSortModelChange(newSortModel);
          setSortCount(newSortCount);
        }
      }}
      pageSizeOptions={[20, 50, 100, 250, 500]}
      pagination
      paginationMode={paginationMode}
      paginationModel={pagination}
      rowCount={rowCount}
      rows={rows}
      rowSelectionModel={selection}
      scrollbarSize={50}
      slots={{
        loadingOverlay: () => <TableOverlay subtitle="One moment please..." title="Loading Companies" />,
      }}
      sortingMode={sortingMode}
      sortingOrder={["asc", "desc"]}
      sortModel={sortModel}
      sx={tableStyles.root}
    />
  );
}
